// Book Sheet Cards — category/tier constants and the client-side grouping
// logic that stands in for section-based display (see book-sheet-cards-spec.md
// Part 1/2/6). The add-cards endpoint always files new entries into a single
// "Unsorted" section (ensure_unsorted_section) — Book Sheet cards are grouped
// and organized by the `category` field directly, ignoring section structure.

// Tier 1 — always on the grid. Tier 2 — behind "see more" until that
// category has a note. Filled Tier 2 categories show after the defaults,
// in this order. Empty Tier 2 categories stay behind "see more".
// Order matters: it's display order in the grid.
export const TIER1_CATEGORIES = ['Character', 'World', 'Conflict', 'Theme', 'Plot'];

/** Display label. Category value stays 'Plot' in the database.
 *  The pile tile is Logline. The sitting door is The book's argument.
 *  Do not hang that door label on this card. Conflict stays Conflict. */
export function categoryDisplayName(category) {
  return (category || '').trim() === 'Plot' ? 'Logline' : category;
}

/** Stable id for a category tile and its left-nav link. */
export function bookNoteCategoryDomId(category) {
  const slug = String(category || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `book-note-${slug || 'note'}`;
}
export const TIER2_CATEGORIES = ['Subplot', 'Relationship', 'Setting/Location'];

// Singleton categories hold exactly one card. Multi-instance categories are
// containers holding any number of independently named cards.
const SINGLETON = new Set(['world', 'conflict', 'theme', 'plot']);
export const isSingletonCategory = (category) => SINGLETON.has((category || '').trim().toLowerCase());
export const isMultiInstanceCategory = (category) => !isSingletonCategory(category);

/** Latest singleton card when more than one row exists. No picker — updated_at, then created_at. */
export function latestSingletonEntry(entries) {
  const list = entries || [];
  if (list.length <= 1) return list[0] || null;
  return [...list].sort((a, b) => {
    const ta = Date.parse(a.updated_at || a.created_at || 0) || 0;
    const tb = Date.parse(b.updated_at || b.created_at || 0) || 0;
    return tb - ta;
  })[0];
}

const norm = (s) => (s || '').trim().toLowerCase();

// Maps a Book Sheet category to the closest existing Focus-session template
// key (config/session_templates.py FOCUS_TEMPLATES — server-validated, so
// only these exact strings are accepted). Character/World/Plot have direct
// matches. Conflict and the multi-instance Tier 2 categories have no
// dedicated template; 'plot' is the closest existing frame for any of them
// (all are plot-adjacent per Part 9's seed questions), and focus_target_name
// carries the real subject so the session still opens scoped correctly.
// Theme maps to 'workshop_theme' — an existing dedicated guided workshop,
// a better match than a bare scoped chat.
const FOCUS_TEMPLATE_BY_CATEGORY = {
  character: 'character',
  world: 'world',
  plot: 'plot',
  conflict: 'plot',
  theme: 'workshop_theme',
  subplot: 'plot',
  relationship: 'character',
  'setting/location': 'world',
};

export function focusTemplateForCategory(category) {
  return FOCUS_TEMPLATE_BY_CATEGORY[norm(category)] || 'plot';
}

// ─── Grouping entries fetched from GET /stories/{id}/bible ─────────────────

/**
 * Flattens every section's entries and buckets them for Book Sheet display,
 * ignoring section structure entirely (Book Sheet's own primitive, not the
 * old free-form section model). Custom cards (tier === 'custom') get their
 * own bucket regardless of category value — everything else buckets by
 * category (case-insensitive) into tier1/tier2/other. Entries with no
 * category at all (pre-Book-Sheet legacy rows) are excluded from every
 * bucket — Book Sheet has nothing to organize them by, and per the
 * disposition audit no real user has confirmed data in this table yet.
 */
export function bucketEntries(bible) {
  const allEntries = (bible?.sections || []).flatMap((section) => section.entries || []);

  const tier1 = {};
  const tier2 = {};
  const other = {};
  const custom = [];

  for (const entry of allEntries) {
    const category = (entry.category || '').trim();
    if (!category) continue; // legacy, uncategorized — not part of Book Sheet's IA
    // Sidecar from Bring into this book (#900). Not Book Theme. Not a tile.
    if (norm(category) === 'series_theme_context') continue;

    if (norm(entry.tier) === 'custom') {
      custom.push(entry);
      continue;
    }

    const tier1Match = TIER1_CATEGORIES.find((c) => norm(c) === norm(category));
    const tier2Match = TIER2_CATEGORIES.find((c) => norm(c) === norm(category));
    if (tier1Match) {
      (tier1[tier1Match] = tier1[tier1Match] || []).push(entry);
    } else if (tier2Match) {
      (tier2[tier2Match] = tier2[tier2Match] || []).push(entry);
    } else {
      // Category set, tier not 'custom', but doesn't match a known Tier
      // 1/2 name — shouldn't happen from this build's own UI, but don't
      // silently drop real data if it does.
      (other[category] = other[category] || []).push(entry);
    }
  }

  return { tier1, tier2, other, custom };
}

/** Groups a category's entries by instance_name (falling back to name, then
 * "Unnamed") for multi-instance containers like Character. */
export function groupByInstance(entries) {
  const groups = {};
  for (const entry of entries || []) {
    const key = (entry.instance_name || entry.name || '').trim() || 'Unnamed';
    (groups[key] = groups[key] || []).push(entry);
  }
  return groups;
}
